import { isApiError } from '@saay/api-client';
import { formatToman, toPersianDigits } from '@saay/formatting';
import {
  EntityPicker,
  FactList,
  Ltr,
  MoneyField,
  SearchField,
  SegmentedControl,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import type { FinancialAdjustmentPosted, FinancialAdjustmentRequest } from '../api.js';
import { approvalSubmitLabel, canUse } from '../permissions.js';
import { setupKeys, useAdminSelf, useLedgerInstallmentTargets } from '../queries.js';
import { reasonSchema } from '../validation.js';
import { CommandDialog } from './command-dialog.js';
import { onSheetOpenChange, useCloseSheet } from './route-sheet.js';
import { FormReasonChips } from './reason-field.js';

/**
 * The `FINANCIAL_ADJUSTMENT` request (AC §18.11 F1, LEDGER §18, D-61): a correction of one
 * installment's principal receivable. A manager posts it at once (`201 FinancialAdjustmentPosted`);
 * anyone else still receives `202`. Accounting only: the contract, its installments, and the
 * allocation never change (Q-S3-01).
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The installment the correction posts to (LEDGER §2 dimensions), with its labels for the header. */
export interface AdjustmentContext {
  readonly buyerId: string;
  readonly creditAllocationId: string;
  readonly installmentId: string;
  readonly saleId: string;
  readonly balanceIrr?: number;
  readonly saleReference?: string;
  readonly sequence?: number;
}

function installmentPickerLabel(
  saleReference: string,
  sequence: number,
  balanceIrr: number,
): string {
  return `فروش ${saleReference} · قسط ${toPersianDigits(sequence)} · مانده ${formatToman(balanceIrr)}`;
}

function AdjustmentTargetFields({
  context,
  form,
  known,
}: {
  readonly context: Partial<AdjustmentContext>;
  readonly form: UseFormReturn<AdjustmentValues>;
  readonly known: boolean;
}) {
  const [saleQuery, setSaleQuery] = useState(context.saleReference ?? '');
  const targets = useLedgerInstallmentTargets(saleQuery);
  const options = useMemo(
    () =>
      (targets.data ?? []).map((row) => ({
        description: formatToman(row.balanceIrr),
        id: row.installmentId,
        label: installmentPickerLabel(row.saleReference, row.sequence, row.balanceIrr),
      })),
    [targets.data],
  );
  const selectedId = form.watch('installmentId') || null;

  useEffect(() => {
    if (!known) return;
    const fields = {
      buyerId: context.buyerId ?? '',
      creditAllocationId: context.creditAllocationId ?? '',
      installmentId: context.installmentId ?? '',
      saleId: context.saleId ?? '',
    };
    for (const [key, value] of Object.entries(fields)) {
      if (value !== '') form.setValue(key as keyof AdjustmentValues, value);
    }
  }, [context, form, known]);

  if (known) {
    const label =
      context.saleReference !== undefined && context.sequence !== undefined
        ? installmentPickerLabel(context.saleReference, context.sequence, context.balanceIrr ?? 0)
        : context.installmentId;
    return (
      <FactList
        items={[
          { key: 'account', label: 'حساب', value: 'اصل مطالبه از خریدار' },
          { key: 'installment', label: 'قسط', value: label },
        ]}
      />
    );
  }

  return (
    <>
      <SearchField
        label="شماره فروش"
        onChange={(event) => setSaleQuery(event.target.value)}
        placeholder="SS-1405-000123"
        value={saleQuery}
      />
      <EntityPicker
        emptyMessage="اول شماره فروش را وارد کنید."
        label="قسط"
        loading={targets.isFetching}
        onQueryChange={() => undefined}
        onSelect={(id) => {
          const row = (targets.data ?? []).find((item) => item.installmentId === id);
          if (row === undefined) return;
          form.setValue('buyerId', row.buyerId);
          form.setValue('creditAllocationId', row.creditAllocationId);
          form.setValue('saleId', row.saleId);
          form.setValue('installmentId', row.installmentId);
        }}
        options={options}
        query=""
        selectedId={selectedId}
      />
    </>
  );
}

const uuid = (label: string) =>
  z
    .string()
    .trim()
    .refine((value) => UUID.test(value), `${label} را کامل وارد کنید.`);

const adjustmentSchema = z.object({
  amountIrr: z
    .number()
    .int()
    .nullable()
    .refine((value) => value !== null && value >= 1, 'مبلغ اصلاح را وارد کنید.'),
  buyerId: uuid('شناسهٔ خریدار'),
  creditAllocationId: uuid('شناسهٔ تخصیص'),
  direction: z.enum(['INCREASE', 'DECREASE']),
  installmentId: uuid('شناسهٔ قسط'),
  reason: reasonSchema(),
  saleId: uuid('شناسهٔ فروش'),
  sourceReference: z
    .string()
    .trim()
    .min(1, 'مستند توجیه اصلاح را بنویسید.')
    .max(200, 'حداکثر ۲۰۰ کاراکتر.'),
});
type AdjustmentValues = z.input<typeof adjustmentSchema>;

function adjustmentGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'FINANCIAL_ADJUSTMENT_EXCEEDS_BALANCE':
      return typeof details.balanceIrr === 'number'
        ? `مانده این قسط ${formatToman(details.balanceIrr)} است؛ کاهش بیش از مانده ممکن نیست.`
        : 'کاهش بیش از مانده ممکن نیست.';
    case 'APPROVAL_REQUEST_PENDING':
      return typeof details.approvalRequestId === 'string' ? (
        <>
          {'برای این قسط اصلاح دیگری در انتظار تأیید است. '}
          <Link to={`/approvals/${details.approvalRequestId}`}>مشاهدهٔ درخواست</Link>
        </>
      ) : (
        'برای این قسط اصلاح دیگری در انتظار تأیید است.'
      );
    case 'APPROVAL_SELF_DECISION_FORBIDDEN':
      return 'درخواست اصلاح حساب خودتان مجاز نیست.';
    default:
      return undefined;
  }
}

export function FinancialAdjustmentDialog({
  context,
  onOpenChange,
}: {
  readonly context: Partial<AdjustmentContext>;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const self = useAdminSelf().data;
  const known = context.installmentId !== undefined && UUID.test(context.installmentId);
  return (
    <CommandDialog<AdjustmentValues, FinancialAdjustmentRequest, FinancialAdjustmentPosted>
      defaultValues={{
        amountIrr: null,
        buyerId: context.buyerId ?? '',
        creditAllocationId: context.creditAllocationId ?? '',
        direction: 'DECREASE',
        installmentId: context.installmentId ?? '',
        reason: '',
        saleId: context.saleId ?? '',
        sourceReference: '',
      }}
      eyebrow={
        context.saleReference === undefined ? (
          'اصلاح مالی'
        ) : (
          <>
            <Ltr>{context.saleReference}</Ltr>
            {context.sequence === undefined ? '' : ` · قسط ${toPersianDigits(context.sequence)}`}
          </>
        )
      }
      // The server names `dimensions.*`; they land on the dimension inputs when those are shown
      // (otherwise, and for `direction`, which has no message of its own, they are listed).
      fieldAliases={
        known
          ? {}
          : {
              'dimensions.buyerId': 'buyerId',
              'dimensions.creditAllocationId': 'creditAllocationId',
              'dimensions.installmentId': 'installmentId',
              'dimensions.saleId': 'saleId',
            }
      }
      fields={['amountIrr', 'sourceReference', 'reason']}
      guidance={adjustmentGuidance}
      name="ledger.financial-adjustment"
      onApprovalPending={async () => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.approvals });
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.approvals });
        toast.success({
          confirmedBy: result,
          description: `مانده پس از ثبت ${formatToman(result.data.balanceAfterIrr)}`,
          title: 'اصلاح مالی ثبت و اجرا شد',
        });
        onOpenChange(false);
      }}
      schema={adjustmentSchema as never}
      send={(client, body, intent) => client.post('/admin/financial-adjustments', { body, intent })}
      size="wide"
      submitLabel={approvalSubmitLabel(self ?? {})}
      title="درخواست اصلاح مالی قسط"
      toBody={(values) => ({
        accountCode: 'BUYER_PRINCIPAL_RECEIVABLE',
        amountIrr: values.amountIrr as number,
        dimensions: {
          buyerId: values.buyerId.trim(),
          creditAllocationId: values.creditAllocationId.trim(),
          installmentId: values.installmentId.trim(),
          saleId: values.saleId.trim(),
        },
        direction: values.direction,
        reason: values.reason.trim(),
        sourceReference: values.sourceReference.trim(),
      })}
    >
      {(form) => (
        <>
          <AdjustmentTargetFields context={context} form={form} known={known} />
          <Controller
            control={form.control}
            name="direction"
            render={({ field }) => (
              <SegmentedControl
                label="جهت اصلاح"
                onChange={field.onChange}
                options={[
                  { key: 'DECREASE', label: 'کاهش مانده' },
                  { key: 'INCREASE', label: 'افزایش مانده' },
                ]}
                value={field.value}
              />
            )}
          />
          <Controller
            control={form.control}
            name="amountIrr"
            render={({ field, fieldState }) => (
              <MoneyField
                error={fieldState.error?.message}
                label="مبلغ اصلاح"
                onChangeRials={field.onChange}
                valueRials={field.value}
              />
            )}
          />
          <TextField
            error={form.formState.errors.sourceReference?.message}
            hint="سندی که اصلاح را توجیه می‌کند، مثل شمارهٔ صورت‌مغایرت"
            label="مستند منبع"
            maxLength={200}
            {...form.register('sourceReference')}
          />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل اصلاح"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="adjustment." />
        </>
      )}
    </CommandDialog>
  );
}

/**
 * `/audit/financial-adjustments/new`: the form over «حسابرسی و دفترکل», opened with the installment's
 * dimensions in the query (`buyerId`, `creditAllocationId`, `saleId`, `installmentId`, and the labels
 * `saleReference`, `sequence`). Installment screens arrive with Stage 4 and link here.
 */
export function FinancialAdjustmentSheet() {
  const [params] = useSearchParams();
  const self = useAdminSelf();
  const close = useCloseSheet('/audit');
  if (self.data === undefined || !canUse(self.data, 'requestFinancialAdjustment')) {
    return null;
  }
  const value = (key: string) => params.get(key) ?? undefined;
  const sequence = Number(params.get('sequence'));
  const context: Partial<AdjustmentContext> = {
    ...(value('buyerId') === undefined ? {} : { buyerId: value('buyerId')! }),
    ...(value('creditAllocationId') === undefined
      ? {}
      : { creditAllocationId: value('creditAllocationId')! }),
    ...(value('saleId') === undefined ? {} : { saleId: value('saleId')! }),
    ...(value('installmentId') === undefined ? {} : { installmentId: value('installmentId')! }),
    ...(value('saleReference') === undefined ? {} : { saleReference: value('saleReference')! }),
    ...(Number.isInteger(sequence) && sequence > 0 ? { sequence } : {}),
  };
  return <FinancialAdjustmentDialog context={context} onOpenChange={onSheetOpenChange(close)} />;
}
